import React, { useEffect, useRef, useState } from 'react';
import { getAuthToken, getUserFromToken } from '../../../context/authContext';
import Alerts from '../../../shared/components/ui/Alerts';
import FormField from './FormField';
import { formService } from '../../../services/formService.js';
import { HttpError } from '../../../services/http/httpClient.js';
import { storageImageService } from '../../../services/storageImageService.js';
import { taskService } from '../../../services/tasks/taskService.js';
import {
  X,
  CheckCircle,
  Warning
} from '@phosphor-icons/react';

const IMAGE_UPLOAD_LIMIT_EXCEEDED_MESSAGE = 'No es posible enviar el formulario, ha superado el limite de 20 imágenes.';

const isImageUploadLimitError = (error) => (
  error instanceof HttpError
    ? error.status === 406
    : error?.status === 406
);

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => resolve({ name: file.name, data: reader.result, type: file.type });
  reader.onerror = (error) => reject(error);
});

const base64ToFile = (base64Obj) => {
  try {
    const arr = base64Obj.data.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[arr.length - 1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], base64Obj.name, { type: mime });
  } catch (error) {
    console.error('Error converting base64 to File', error);
    return null;
  }
};

const initializeFormData = (fields, savedData = {}) => {
  const initialData = {};

  fields.forEach((field) => {
    if (savedData[field.id] !== undefined) {
      if (field.type === 'image') {
        const base64Files = savedData[field.id];
        if (Array.isArray(base64Files)) {
          const files = base64Files
            .map((b64) => base64ToFile(b64))
            .filter((file) => file !== null);
          initialData[field.id] = files;
        } else {
          initialData[field.id] = '';
        }
      } else if (field.type === 'checkbox' || field.type === 'boolean') {
        initialData[field.id] = savedData[field.id] === true;
      } else {
        initialData[field.id] = savedData[field.id];
      }
    } else if (field.type === 'checkbox' || field.type === 'boolean') {
      initialData[field.id] = false;
    } else {
      initialData[field.id] = '';
    }
  });

  return initialData;
};

const DynamicForm = ({
  task,
  formType,
  onClose,
  onSuccess,
  title,
  submitButtonText = 'Enviar',
  skipFormSubmit = false
}) => {
  const [form, setForm] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [showAlert, setShowAlert] = useState(false);
  const [alertTitle, setAlertTitle] = useState('Aviso');
  const [alertContent, setAlertContent] = useState('');
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const modalContentRef = useRef(null);

  useEffect(() => {
    fetchForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formType, task?.taskTypeId, task?.id]);

  useEffect(() => {
    if (!loading && form && form.fields && Object.keys(formData).length > 0) {
      const storageKey = `dynamic_form_${task?.id}_${formType}`;

      const saveDataAsync = async () => {
        const dataToSave = {};

        for (const field of form.fields) {
          if (field.type === 'image') {
            const files = formData[field.id];
            if (files && files.length > 0) {
              const base64Files = [];
              for (let i = 0; i < files.length; i += 1) {
                if (files[i] instanceof File) {
                  try {
                    const b64 = await fileToBase64(files[i]);
                    base64Files.push(b64);
                  } catch (error) {
                    console.error('Error convirtiendo archivo a base64', error);
                  }
                }
              }
              dataToSave[field.id] = base64Files;
            }
          } else {
            dataToSave[field.id] = formData[field.id];
          }
        }

        try {
          localStorage.setItem(storageKey, JSON.stringify(dataToSave));
        } catch (error) {
          console.error('Error al guardar en localStorage (posible limite de cuota por imagenes):', error);
          const dataWithoutImages = { ...dataToSave };
          form.fields.forEach((field) => {
            if (field.type === 'image') {
              delete dataWithoutImages[field.id];
            }
          });

          try {
            localStorage.setItem(storageKey, JSON.stringify(dataWithoutImages));
          } catch (fallbackError) {
            console.error('Error fallback guardando localStorage', fallbackError);
          }
        }
      };

      saveDataAsync();
    }
  }, [formData, loading, form, task?.id, formType]);

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });

    setTimeout(() => {
      if (modalContentRef.current) {
        modalContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 100);

    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success' });
    }, 3000);
  };

  const fetchForm = async () => {
    try {
      setLoading(true);

      const result = await formService.getFormsByTaskType(task.taskTypeId, formType);
      const customForm = result && result.length > 0 ? result[0] : null;

      if (!customForm) {
        setAlertTitle('Formulario no configurado');
        setAlertContent('El tipo de tarea seleccionada no tiene un formulario asociado. Por favor comuníquese con un administrador para corregir este problema.');
        setShowAlert(true);
        setForm(null);
        return;
      }

      setForm(customForm);

      const storageKey = `dynamic_form_${task?.id}_${formType}`;
      let savedData = {};
      try {
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          savedData = JSON.parse(stored);
        }
      } catch (error) {
        console.error('Error al leer de localStorage:', error);
      }

      setFormData(initializeFormData(customForm.fields || [], savedData));
      setErrors({});
    } catch (error) {
      console.error('Error fetching form:', error);
      setAlertTitle('Error al cargar formulario');
      setAlertContent('El tipo de tarea seleccionada no tiene un formulario asociado. Por favor comuníquese con un administrador para corregir este problema.');
      setShowAlert(true);
      setForm(null);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (index, value) => {
    const field = form.fields[index];
    setFormData((previous) => ({
      ...previous,
      [field.id]: value
    }));

    if (errors[field.id]) {
      setErrors((previous) => {
        const nextErrors = { ...previous };
        delete nextErrors[field.id];
        return nextErrors;
      });
    }
  };

  const validateForm = () => {
    const nextErrors = {};

    form.fields.forEach((field) => {
      const value = formData[field.id];

      if (field.required) {
        if (field.type === 'image') {
          if (!value || (Array.isArray(value) && value.length === 0)) {
            nextErrors[field.id] = `El campo ${field.id} es requerido`;
          }
        } else if (field.type === 'checkbox' || field.type === 'boolean') {
          if (value === undefined || value === null) {
            nextErrors[field.id] = `El campo ${field.id} es requerido`;
          }
        } else if (!value || (typeof value === 'string' && value.trim() === '')) {
          nextErrors[field.id] = `El campo ${field.id} es requerido`;
        }
      }

      if (value && field.validation) {
        if (field.type === 'image') {
          const filesToValidate = Array.isArray(value) ? value : [];

          if (field.validation.maxFiles && filesToValidate.length > field.validation.maxFiles) {
            nextErrors[field.id] = `Máximo ${field.validation.maxFiles} archivos permitidos`;
          }

          filesToValidate.forEach((file) => {
            if (field.validation.maxSize && file.size > field.validation.maxSize * 1024 * 1024) {
              nextErrors[field.id] = `${nextErrors[field.id] ? `${nextErrors[field.id]}, ` : ''}El archivo ${file.name} excede el tamaño máximo de ${field.validation.maxSize}MB`;
            }

            if (field.accept && !field.accept.split(',').some((type) => {
              if (type.includes('*')) {
                return file.type.startsWith(type.replace('*', ''));
              }
              return file.type === type.trim();
            })) {
              nextErrors[field.id] = `${nextErrors[field.id] ? `${nextErrors[field.id]}, ` : ''}El archivo ${file.name} no es del tipo permitido`;
            }
          });
        } else {
          if (field.validation.minLength && value.length < field.validation.minLength) {
            nextErrors[field.id] = `Mínimo ${field.validation.minLength} caracteres en observaciones`;
          }
          if (field.validation.maxLength && value.length > field.validation.maxLength) {
            nextErrors[field.id] = `Máximo ${field.validation.maxLength} caracteres`;
          }
          if (field.validation.min && parseFloat(value) < field.validation.min) {
            nextErrors[field.id] = `Valor mínimo: ${field.validation.min}`;
          }
          if (field.validation.max && parseFloat(value) > field.validation.max) {
            nextErrors[field.id] = `Valor máximo: ${field.validation.max}`;
          }
        }
      }
    });

    setErrors(nextErrors);
    const isValid = Object.keys(nextErrors).length === 0;

    if (!isValid) {
      const firstError = Object.values(nextErrors)[0];
      showNotification(firstError, 'error');
    } else {
      showNotification('Validación completada correctamente', 'success');
    }

    return isValid;
  };

  const uploadImageFields = async () => {
    const imageFields = form.fields.filter((field) => field.type === 'image');
    const imageUrls = {};

    for (const field of imageFields) {
      const files = formData[field.id];
      if (files && files.length > 0) {
        try {
          const currentUser = getUserFromToken();
          const technicianId = currentUser?.email || 'unknown';
          const folderName = `${form.id}_${task.id}_${technicianId}_${field.name}`;
          imageUrls[field.id] = await storageImageService.uploadStorageImages({
            folderName,
            files
          });
        } catch (error) {
          throw error;
        }
      }
    }

    return {
      ...formData,
      ...imageUrls
    };
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      const submissionFormData = await uploadImageFields();

      if (skipFormSubmit) {
        showNotification('Formulario guardado correctamente', 'success');
        try {
          localStorage.removeItem(`dynamic_form_${task?.id}_${formType}`);
          localStorage.removeItem(`dynamic_form_${task?.id}_cancelacion`);
          await onSuccess({ formData: submissionFormData, formId: form.id, result: null });
        } catch (callbackError) {
          console.error('[DynamicForm] Error en callback onSuccess:', callbackError);
          throw callbackError;
        }
        return;
      }

      const token = getAuthToken();
      if (!token) {
        throw new Error('No hay token de autenticación. Por favor, inicia sesión nuevamente.');
      }

      const formDataToSend = new FormData();
      formDataToSend.append('formId', form.id);
      formDataToSend.append('taskId', task.id);
      formDataToSend.append('formType', formType);
      formDataToSend.append('technicianName', task.technicianName || 'Técnico');
      formDataToSend.append('clientName', task.client?.name || task.client?.email || 'Cliente');
      formDataToSend.append('formData', JSON.stringify(submissionFormData));

      const result = await taskService.completeTaskForm(task.id, formDataToSend);

      if (result && (result.success || result.message)) {
        if (formType === 'cancelacion') {
          await taskService.cancelTask(task.id);
        }

        showNotification('Formulario enviado correctamente', 'success');
        try {
          localStorage.removeItem(`dynamic_form_${task?.id}_cancelacion`);
          localStorage.removeItem(`dynamic_form_${task?.id}_finalizacion`);
          await onSuccess({ formData: submissionFormData, formId: form.id, result });
        } catch (callbackError) {
          console.error('[DynamicForm] Error en callback onSuccess:', callbackError);
          throw callbackError;
        }
      } else {
        throw new Error('Error al enviar el formulario');
      }
    } catch (error) {
      console.error('[DynamicForm] Error submitting form:', error);
      console.error('[DynamicForm] Error stack:', error.stack);

      if (isImageUploadLimitError(error)) {
        setShowAlert(false);
        showNotification(IMAGE_UPLOAD_LIMIT_EXCEEDED_MESSAGE, 'error');
        return;
      }

      if (error.message.includes('token') || error.message.includes('autentic')) {
        setAlertTitle('Error de Autenticación');
        setAlertContent('Error de autenticación. Por favor, inicia sesión nuevamente.');
      } else {
        setAlertTitle('Error');
        setAlertContent(error.message || 'Error al enviar el formulario. Por favor, inténtalo de nuevo.');
      }
      setShowAlert(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-[var(--app-overlay)] flex items-center justify-center z-[9999]">
        <div className="bg-surface rounded-lg p-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber mx-auto" />
          <p className="mt-2 text-content-muted">Cargando formulario...</p>
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="fixed inset-0 bg-[var(--app-overlay)] flex items-center justify-center z-[9999]">
        <div className="bg-surface rounded-lg p-6 max-w-md">
          <div className="text-center">
            <Warning className="w-12 h-12 text-warning mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-content mb-2">Formulario no encontrado</h3>
            <p className="text-content-muted mb-4">
              No se encontró un formulario de {formType} configurado.
            </p>
            <button
              onClick={onClose}
              className="bg-amber text-on-amber px-4 py-2 rounded-lg hover:bg-amber-hover"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    );
  }

  const displayTitle = title || form.name || `${formType.charAt(0).toUpperCase() + formType.slice(1)} - ${task.client?.name || task.client?.email || 'Cliente'}`;

  return (
    <div className="fixed inset-0 bg-[var(--app-overlay)] flex items-center justify-center z-[9999] p-4">
      <div ref={modalContentRef} className="bg-surface rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto relative">
        <div className="flex items-center justify-between p-6 border-b border-line">
          <div>
            <h2 className="text-xl font-semibold text-content">
              {displayTitle}
            </h2>
            <p className="text-sm text-content-muted mt-1">
              {form.description}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-content-muted hover:text-content transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {notification.show && (
          <div
            className={`mx-6 mt-4 rounded-lg border-l-4 p-4 flex items-center gap-3 ${notification.type === 'success'
              ? 'bg-success/12 border-success text-success'
              : 'bg-error/12 border-error text-error'
            }`}
            role="alert"
          >
            <div className="text-2xl leading-none">
              {notification.type === 'success' ? 'OK' : ''}
            </div>
            <div className="text-base font-medium leading-snug">
              {notification.message}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {form.fields && Array.isArray(form.fields) && form.fields.length > 0 ? (
            form.fields.map((field, index) => (
              <FormField
                key={`${form.id}_${field.name || field.id}_${index}`}
                field={field}
                index={index}
                value={formData[field.id]}
                onChange={handleFieldChange}
                formId={form.id}
              />
            ))
          ) : (
            <p className="text-content-muted">No hay campos para mostrar</p>
          )}

          <div className="flex justify-end space-x-3 pt-6 border-t border-line">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-content bg-surface-2 rounded-lg hover:bg-line transition-colors"
              disabled={submitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-2 bg-amber text-on-amber rounded-lg hover:bg-amber-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-on-amber mr-2" />
                  Enviando...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {submitButtonText}
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {showAlert && (
        <Alerts
          title={alertTitle}
          onClose={() => setShowAlert(false)}
        >
          {alertContent}
        </Alerts>
      )}
    </div>
  );
};

export default DynamicForm;
